import React, { useEffect, useMemo, useRef, useState } from 'react';

import 'twin.macro';
import AppPage from 'components/page/app-page/AppPage';
import SaveButton from 'components/save-button/SaveButton';
import VacancyPreviewer from 'components/previewers/VacancyPreviewer';
import useNavigationContext from 'hooks/context/nav-context';
import useVacancyContext from 'hooks/context/vacancy-context';
import { CopyToClipboard } from 'components/copy-to-clipboard/CopyToClipboard';
import { useLazyQuery } from 'hooks/sympl-query';
import { GET_JOBTYPES_SUBJOBTYPES } from 'graphql/jobtypes/queries';
import { EXPERIENCE, JobType } from 'types/targetingTypes';
import Button from 'components/button/Button';
import InputGroup from 'components/form/input-group/InputGroup';
import PillGroup from 'components/form/pill-group/PillGroup';
import { Modal } from 'components/page/app-page/Modal';
import { Title1, Title3 } from 'components/typography/Typography';
import { Controller, useForm } from 'react-hook-form';
import Input from 'components/form/input/Input';
import useApiList from 'hooks/useApiList';
import { isTextEditorEmpty, popConfetti, stripHtml } from 'utils/baseHelpers';
import useGetStartedContext from 'hooks/context/get-started-context';
import MissingFields from 'components/page/app-page/MissingFields';
import { generateVacancy } from 'utils/vacancy-generator/vacancyHelpers';
import { ArrowSquareUpRight, Robot, User } from '@phosphor-icons/react';
import { Routes } from 'types/routeTypes';
import { useSymplCookie } from 'hooks/symplCookie';
import { fireEvent } from 'utils/eventHelper';
import CustomUrlForm from '../custom-url/CustomUrlForm';
import CustomUrlPage from '../custom-url/CustomUrlPage';
import { useNavigate } from 'react-router-dom';
import { PostHogFeature } from 'posthog-js/react';
import SalaryEditor from 'components/vacancy/SalaryEditor';
import { SUBSCRIPTION_CAPABILITIES } from 'types/subscriptions/types';
import { validateSalary } from 'utils/salaryHelpers';

export type TEditorModalTypes =
  | 'custom-vacancy-url'
  | 'custom-vacancy-url-no-back'
  | 'first-time'
  | 'ai';

const EditorPage: React.FC = () => {
  const navigate = useNavigate();

  const [usedAiGen, setUsedAiGen] = useSymplCookie('usedAiGen');

  const {
    copyLoading,
    saveHandler,
    copyData,
    setCopyData,
    generatingFields,
    setGeneratingFields,
    setPayload,
  } = useVacancyContext();

  const {
    activeVacancy,
    setHasUnsavedChanges,
    hasUnsavedChanges,
    brands,
    hasSubscriptionCapability,
    currentVacancy,
    refetchCurrentVacancy,
  } = useNavigationContext();

  const salaryFeatureEnabled = hasSubscriptionCapability(
    SUBSCRIPTION_CAPABILITIES.PAY_TRANSPARENCY_BASE
  );
  const salaryRequired =
    salaryFeatureEnabled && currentVacancy?.brand?.salary_mandatory === true;
  const [salaryValidationErrors, setSalaryValidationErrors] = useState<
    ReturnType<typeof validateSalary>['errors']
  >({});

  const { nextItem, refetchCheckList } = useGetStartedContext();

  const { control, handleSubmit } = useForm();

  const { data: languages } = useApiList('languages');

  const formData = useRef({
    companyDescription: '',
    companyOffers: ['', '', ''],
    toneOfVoice: [''],
  });
  const showedFirstTimeOnce = useRef(false);

  const [showModal, setShowModal] = useState<TEditorModalTypes>();

  const [fetchJobTypes] = useLazyQuery<{ jobTypes: JobType[] }, {}>(
    GET_JOBTYPES_SUBJOBTYPES
  );

  const validateSalaryBeforeSave = () => {
    if (!salaryRequired || !copyData) {
      setSalaryValidationErrors({});
      return true;
    }

    const rangeEnabled =
      copyData.salary_min !== null &&
      copyData.salary_min !== undefined &&
      copyData.salary_max !== copyData.salary_min;

    const result = validateSalary({
      salary_min: copyData.salary_min,
      salary_max: copyData.salary_max,
      currency: copyData.currency,
      pay_period: copyData.pay_period,
      salary_note: copyData.salary_note,
      rangeEnabled,
      mandatory: true,
    });

    setSalaryValidationErrors(result.errors);
    return Object.keys(result.errors).length === 0;
  };

  const checkField = (condition: boolean, message: string) =>
    condition ? [message] : [];

  const missingFields = useMemo(() => {
    if (!copyData) return [];

    return [
      ...checkField(!copyData.pictures?.logo, 'No logo set'),
      ...checkField(!copyData.pictures?.image_cb1, 'No first visual set'),
      ...checkField(!copyData.pictures?.image_cb3, 'No second visual set'),
      ...checkField(!copyData.title, 'No title set'),
      ...checkField(
        !copyData.vac_text_essence ||
          isTextEditorEmpty(copyData.vac_text_essence),
        'No essence text set'
      ),
      ...checkField(!copyData.todo_title, 'No todo title set'),
      ...checkField(
        !copyData.vac_text_todo || isTextEditorEmpty(copyData.vac_text_todo),
        'No todo text set'
      ),
      ...checkField(!copyData.expectations_title, 'No expectations title set'),
      ...checkField(
        !copyData.vac_text_expectations ||
          isTextEditorEmpty(copyData.vac_text_expectations),
        'No expectations text set'
      ),
      ...checkField(!copyData.offer_title, 'No offer title set'),
      ...checkField(
        !copyData.vac_text_offer || isTextEditorEmpty(copyData.vac_text_offer),
        'No offer text set'
      ),
      ...checkField(!copyData.company_title, 'No company title set'),
      ...checkField(
        !copyData.vac_text_company ||
          isTextEditorEmpty(copyData.vac_text_company),
        'No company text set'
      ),
    ];
  }, [copyData]);

  const validTargetingData = useMemo(() => {
    const targeting = currentVacancy?.targeting;
    return (
      !!targeting?.vac_name &&
      !!targeting?.brand_id &&
      !!targeting?.locations &&
      !!targeting?.locations.length &&
      !!targeting?.job_type
    );
  }, [currentVacancy?.targeting]);

  useEffect(() => {
    if (
      !currentVacancy?.has_custom_url &&
      !copyData?.vac_url &&
      missingFields.filter((field) => field.includes('text')).length > 2 &&
      validTargetingData &&
      showedFirstTimeOnce.current === false &&
      generatingFields.length === 0
    ) {
      setShowModal('first-time');
      showedFirstTimeOnce.current = true;
    }
  }, [
    missingFields,
    copyData,
    validTargetingData,
    showedFirstTimeOnce,
    generatingFields,
  ]);

  const onSubmit = async ({
    'company-description': companyDescription,
    'company-offers': offers,
    'tone-of-voice': toneOfVoice,
  }: {
    'company-description': string;
    'company-offers': string[];
    'tone-of-voice': string[];
  }) => {
    setShowModal(undefined);
    setGeneratingFields([
      'vac_text_essence',
      'vac_text_uniqueness',
      'vac_text_expectations',
      'vac_text_todo',
      'vac_text_offer',
      'vac_text_company',
    ]);

    // Persist state
    formData.current = {
      companyDescription,
      companyOffers: offers,
      toneOfVoice: toneOfVoice,
    };

    // Fetch data used for AI generation
    const { data: { jobTypes } = {} } = await fetchJobTypes();

    const targeting = currentVacancy?.targeting;

    const jobType = jobTypes?.find(({ id }) => id === targeting?.job_type);

    const jobCategory = targeting?.sub_job_type
      ? jobType?.sub_job_types?.find(({ id }) => id === targeting?.sub_job_type)
          ?.name
      : jobType?.name;

    const payload = {
      jobFunction: targeting?.vac_name!,
      companyName: brands.find(({ id }) => id === targeting?.brand_id)?.name!,
      location: targeting?.locations?.[0].name!,
      jobCategory: jobCategory!,
      companyDescription,
      experienceLevel:
        targeting?.experience
          ?.map((code) => {
            return EXPERIENCE.find(({ code: c }) => c === code)?.name;
          })
          .join(', ') ?? 'None',
      toneOfVoice: toneOfVoice.join(', '),
      offers,
      language:
        languages.find(({ key }) => key === targeting?.language_id)?.label ??
        'English',
    };

    setPayload(payload);

    await generateVacancy({
      vacancyId: activeVacancy!,
      payload,
      setCopyData,
      setGeneratingFields,
    });

    if (!usedAiGen) {
      popConfetti();
      setUsedAiGen(true);
    }

    fireEvent('ai_copy_popup');
  };

  const onCustomLinkSubmit = ({ isModal }: { isModal: boolean }) => {
    if (isModal) setShowModal(undefined);
    refetchCurrentVacancy();
    refetchCheckList();
  };

  return (
    <AppPage
      heading="Vacancy Page"
      loading={copyLoading}
      disablePadding={true}
      clipboardCopy={
        copyData?.share_url && <CopyToClipboard url={copyData?.share_url} />
      }
      cta={
        <div tw="flex space-x-4">
          {!currentVacancy?.has_custom_url && !!missingFields.length && (
            <MissingFields missingFields={missingFields} />
          )}
          {!hasUnsavedChanges && nextItem?.key === 'typeform' ? (
            <Button
              variant="indigo"
              onClick={() => navigate(Routes.INTERVIEW)}
              testId="vacancy-page-next-step-button"
            >
              Go to next step &rarr;
            </Button>
          ) : (
            <SaveButton
              shouldSave={hasUnsavedChanges}
              loading={copyLoading}
              disabled={!!generatingFields.length}
              labelSkip="Salary fields are required"
              onClick={async () => {
                if (!validateSalaryBeforeSave()) {
                  throw new Error('Salary fields are required');
                }

                await saveHandler(copyData);
              }}
              onDone={() => {
                setHasUnsavedChanges(false);
                refetchCheckList();
              }}
            />
          )}
        </div>
      }
    >
      <Modal
        show={showModal === 'first-time'}
        onClose={() => setShowModal(undefined)}
      >
        <Title1>Let's setup your vacancy page!</Title1>
        <div tw="grid grid-cols-2 gap-4 my-8">
          <button
            type="button"
            tw="p-8 ring-2 ring-gray-200 rounded-md grid items-center hover:(bg-blue-50 ring-blue-500) focus:outline-none"
            onClick={() => setShowModal(undefined)}
          >
            <div tw="space-y-4 text-gray-800">
              <User weight="fill" size={32} tw="m-auto" />
              <p tw="font-semibold">I'll create it myself.</p>
            </div>
          </button>
          <button
            type="button"
            tw="p-8 ring-2 ring-gray-200 rounded-md grid items-center hover:(bg-blue-50 ring-blue-500) focus:outline-none"
            onClick={() => setShowModal('ai')}
          >
            <div tw="space-y-4 text-gray-800" data-testid="vacancy-page-ai-gen">
              <Robot weight="fill" size={32} tw="m-auto" />
              <p tw="font-semibold">Create it for me (using AI).</p>
            </div>
          </button>

          <PostHogFeature flag="byov" match={true}>
            <button
              type="button"
              tw="col-span-2 mx-auto w-max p-8 ring-2 ring-gray-200 rounded-md grid items-center hover:(bg-blue-50 ring-blue-500) focus:outline-none"
              onClick={() => setShowModal('custom-vacancy-url')}
            >
              <div tw="space-y-4 text-gray-800">
                <ArrowSquareUpRight weight="fill" size={32} tw="m-auto" />
                <p tw="font-semibold">Use your own vacancy page</p>
              </div>
            </button>
          </PostHogFeature>
        </div>
      </Modal>
      <Modal show={showModal === 'ai'} onClose={() => setShowModal(undefined)}>
        <form tw="py-3 w-auto sm:w-[35rem]" onSubmit={handleSubmit(onSubmit)}>
          <div tw="flex flex-col space-y-5">
            <Controller
              id="company-description"
              name="company-description"
              control={control}
              defaultValue={
                stripHtml(copyData?.vac_text_company ?? '') ||
                formData.current.companyDescription
              }
              rules={{ required: true }}
              render={({ value, onChange }) => (
                <div>
                  <Title3>Describe your company</Title3>
                  <Input
                    id="vacancy-page-company-description"
                    type="text"
                    rows={4}
                    textarea={true}
                    autoGrow={true}
                    value={value}
                    placeholder={
                      'e.g. SME, team of 15 people, founded in 2015, our product is ...'
                    }
                    onChange={(e) => onChange(e.target.value)}
                  />
                </div>
              )}
            />

            <Controller
              id="company-offers"
              name="company-offers"
              control={control}
              defaultValue={
                copyData?.vac_text_uniqueness?.map((value) => value.value) ||
                formData.current.companyOffers
              }
              rules={{ required: true }}
              render={({ value, onChange }) => (
                <div>
                  <Title3>
                    How would you convince people to apply for this job?
                  </Title3>
                  <InputGroup
                    id={'company-offers'}
                    value={value}
                    minInputs={3}
                    maxInputs={5}
                    onChange={onChange}
                  />
                </div>
              )}
            />

            <Controller
              id="tone-of-voice"
              name="tone-of-voice"
              control={control}
              defaultValue={formData.current.toneOfVoice}
              rules={{ required: true }}
              render={({ value, onChange }) => (
                <div>
                  <Title3>Which tone of voice is best suited?</Title3>
                  <PillGroup
                    selected={value}
                    options={[
                      '💼 Professional',
                      '😊 Friendly',
                      '😃 Enthusiastic',
                      '💪 Confident',
                      '📝 Formal',
                      '💬 Conversational',
                      '🗣 Assertive',
                      '😎 Excited',
                    ]}
                    testId="vacancy-page-tone"
                    onChange={(options) =>
                      onChange(
                        options
                          .filter(({ selected }) => selected)
                          ?.map(({ key }) => key)
                      )
                    }
                  />
                </div>
              )}
            />

            <div tw="ml-auto">
              <Button type="submit" testId="vacancy-page-generate">
                Generate 🪄
              </Button>
            </div>
          </div>
        </form>
      </Modal>
      <Modal
        show={
          showModal === 'custom-vacancy-url' ||
          showModal === 'custom-vacancy-url-no-back'
        }
        onClose={() => setShowModal(undefined)}
      >
        <CustomUrlForm
          onSubmit={() => onCustomLinkSubmit({ isModal: true })}
          showWarning
          {...(showModal === 'custom-vacancy-url' && {
            onBack: () => setShowModal('first-time'),
          })}
        />
      </Modal>
      {currentVacancy?.has_custom_url ? (
        <CustomUrlPage
          onSubmit={() => onCustomLinkSubmit({ isModal: false })}
        />
      ) : (
        <div tw="flex h-full min-h-0 flex-col overflow-hidden">
          {salaryFeatureEnabled && (
            <SalaryEditor
              mandatory={salaryRequired}
              validationErrors={salaryValidationErrors}
            />
          )}
          <div tw="min-h-0 flex-1">
            <VacancyPreviewer
              validTargetingData={validTargetingData}
              setShowModal={setShowModal}
            />
          </div>
        </div>
      )}
    </AppPage>
  );
};

export default EditorPage;
